import type { ReactNode } from 'react';

/** Matches `AssetLibraryStackedThumbnail` / dashboard batch cards. */
export const BATCH_STACK_PEEK_WIDTH_PX = 16;

const BATCH_STACK_LAYER_COUNT = 4;
const BATCH_STACK_LAYER_OFFSET_PX = 4;

const BATCH_STACK_LAYER_CLASS =
  'absolute top-0 left-0 h-full w-full overflow-hidden rounded-lg border-r-2 border-r-white dark:border-r-gray-90';

export const BATCH_STACK_FRONT_LAYER_CLASS =
  'relative z-50 h-full w-full overflow-hidden rounded-lg border-r-2 border-r-white dark:border-r-gray-90';

interface BatchStackedThumbnailShellProps {
  backLayer: ReactNode;
  frontLayer: ReactNode;
}

/**
 * Shared 4-layer offset stack (motion library, batch history, dashboard).
 * Parent should set `paddingRight: BATCH_STACK_PEEK_WIDTH_PX` when stacked.
 */
export default function BatchStackedThumbnailShell({
  backLayer,
  frontLayer,
}: BatchStackedThumbnailShellProps) {
  return (
    <div className="relative h-full w-full">
      {Array.from({ length: BATCH_STACK_LAYER_COUNT }, (_, index) => (
        <div
          key={index}
          aria-hidden
          className={BATCH_STACK_LAYER_CLASS}
          style={{
            left: `${(index + 1) * BATCH_STACK_LAYER_OFFSET_PX}px`,
            zIndex: 49 - index,
          }}
        >
          {backLayer}
        </div>
      ))}
      <div className={BATCH_STACK_FRONT_LAYER_CLASS}>{frontLayer}</div>
    </div>
  );
}

interface BatchStackedThumbnailFrameProps {
  enabled: boolean;
  backplate: ReactNode;
  children: ReactNode;
}

/** Placeholder stack (gray back layers). Peek padding lives on the parent tile. */
export function BatchStackedThumbnailFrame({
  enabled,
  backplate,
  children,
}: BatchStackedThumbnailFrameProps) {
  if (!enabled) {
    return <>{children}</>;
  }

  return (
    <BatchStackedThumbnailShell backLayer={backplate} frontLayer={children} />
  );
}

export function BatchStackedThumbnailGrayBackplate() {
  return <div className="bg-gray-30 dark:bg-gray-70 h-full w-full" />;
}

interface HeroLetterboxTileProps {
  src: string;
  letterboxColor: string;
  onLoadError?: () => void;
}

function HeroLetterboxTile({
  src,
  letterboxColor,
  onLoadError,
}: HeroLetterboxTileProps) {
  return (
    <div className="relative h-full w-full">
      <div
        className="absolute inset-0"
        style={{ backgroundColor: letterboxColor }}
        aria-hidden
      />
      <div className="absolute inset-0 flex items-center justify-center">
        <img
          src={src}
          alt=""
          crossOrigin="anonymous"
          className="max-h-full w-auto max-w-full object-contain"
          onError={onLoadError}
        />
      </div>
    </div>
  );
}

interface BatchHistoryHeroLetterboxMediaProps {
  src: string;
  letterboxColor: string;
  stacked?: boolean;
  onLoadError?: () => void;
}

/** Completed character hero thumb; stack chrome matches `AssetLibraryStackedThumbnail`. */
export function BatchHistoryHeroLetterboxMedia({
  src,
  letterboxColor,
  stacked,
  onLoadError,
}: BatchHistoryHeroLetterboxMediaProps) {
  const tile = (
    <HeroLetterboxTile
      src={src}
      letterboxColor={letterboxColor}
      onLoadError={onLoadError}
    />
  );

  if (!stacked) {
    return tile;
  }

  return <BatchStackedThumbnailShell backLayer={tile} frontLayer={tile} />;
}
